import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Toaster } from "sonner";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { GameProvider } from "@/contexts/GameContext";
// Colyseus migration: replace legacy RealtimeProvider
import { ColyseusProvider } from "@/contexts/ColyseusProvider.jsx";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TranslationProvider } from "@/i18n/useTranslation.jsx";
import { useGame } from "@/hooks/useGame";
import { useIsProductionDomain } from "@/hooks/useDomain";
import { LoginScreen } from "@/views/auth";
import { AppHeader, AppFooter, LoadingSpinner } from "@/components/layout";
import { ColyseusDebugPanel } from "@/components/debug/ColyseusDebugPanel";
import WorkInProgress from "@/views/WorkInProgress";
import "./App.css";
import "@/assets/style/globals.less";

// Lazy load components that are not immediately visible
const TableTop = lazy(() =>
  import("@/views/tabletop").then((module) => ({ default: module.TableTop }))
);
const Lobby = lazy(() =>
  import("@/views/lobby").then((module) => ({ default: module.Lobby }))
);
const GameBoard = lazy(() =>
  import("@/views/game").then((module) => ({ default: module.GameBoard }))
);
const EndGame = lazy(() =>
  import("@/views/game/EndGame").then((module) => ({ default: module.default }))
);
const CardsView = lazy(() =>
  import("@/views/cards").then((module) => ({ default: module.CardsView }))
);

/**
 * Authentication component that redirects unauthenticated users
 */
function AuthWrapper({ children }) {
  const { isAuthenticated } = useGame();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Handler for the TableTop route that clears the session
 */
function TableTopHandler() {
  const { clearCurrentSession } = useGame();

  // Clear session when accessing TableTop
  useEffect(() => {
    clearCurrentSession();
  }, [clearCurrentSession]);

  return (
    <div>
      <AppHeader currentPath="/tabletop" />
      <main data-testid="App-main">
        <Suspense fallback={<LoadingSpinner />}>
          <TableTop />
        </Suspense>
      </main>
      <AppFooter />
    </div>
  );
}

function CardsHandler() {
  const { clearCurrentSession } = useGame();

  // Clear session when accessing Cards
  useEffect(() => {
    clearCurrentSession();
  }, [clearCurrentSession]);

  return (
    <div>
      <AppHeader currentPath="/cards" />
      <main data-testid="App-main" className="container mx-auto px-4 py-6">
        <Suspense fallback={<LoadingSpinner />}>
          <CardsView />
        </Suspense>
      </main>
      <AppFooter />
    </div>
  );
}
/**
 * Wrapper component for the Lobby that extracts tableId from the URL
 */
function LobbyWrapper() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const { activeTables, saveCurrentSession } = useGame();
  const [players, setPlayers] = useState([]);

  // Update session when component mounts
  useEffect(() => {
    if (tableId) {
      saveCurrentSession(tableId, "lobby");
    }
  }, [tableId, saveCurrentSession]);

  /**
   * Handler to start game with selected players
   * @param {Array<Object>} selectedPlayers - List of selected players
   */
  const handleStartGame = useCallback(
    (selectedPlayers) => {
      setPlayers(selectedPlayers);
      // Navigate to game board after starting game
      navigate(`/table/${tableId}/game`);
    },
    [navigate, tableId]
  );

  // Check if table exists before rendering Lobby
  const currentTable = activeTables.find(
    (table) => String(table.id) === String(tableId)
  );

  // If table is not found, redirect to tabletop
  if (!currentTable) {
    console.warn(`[LobbyWrapper] Table not found: ${tableId}`, {
      activeTables,
    });
    return <Navigate to="/tabletop" replace />;
  }

  return (
    <Lobby
      tableId={tableId}
      onStartGame={handleStartGame}
      players={players}
      setPlayers={setPlayers}
    />
  );
}

/**
 * Wrapper component for the GameBoard that extracts tableId from the URL
 */
function GameBoardWrapper() {
  const { tableId } = useParams();
  const { activeTables, saveCurrentSession, playerCharacters, currentUser } =
    useGame();
  const [players, setPlayers] = useState([]);

  // Update session when component mounts
  useEffect(() => {
    if (tableId) {
      saveCurrentSession(tableId, "game");
    }
  }, [tableId, saveCurrentSession]);

  // Check if table exists before rendering GameBoard
  const currentTable = activeTables.find(
    (t) => String(t.id) === String(tableId)
  );

  // Find active table and its players
  useEffect(() => {
    if (currentTable) {
      // Convert player names (strings) to player objects expected by GameBoard
      const playerObjects = currentTable.players.map((playerName, index) => ({
        id: `player-${index}`,
        name: playerName,
        character: "Adventurer", // Default character type
      }));
      setPlayers(playerObjects);
    }
  }, [currentTable]);

  /**
   * Handler to return to lobby
   */
  const handleBackToLobby = () => {
    // Navigation will be handled by context
  };

  // If table is not found, redirect to tabletop
  if (!currentTable) {
    console.warn(`[GameBoardWrapper] Table not found: ${tableId}`, {
      activeTables,
    });
    return <Navigate to="/tabletop" replace />;
  }

  // If table is finished, show EndGame screen
  if (currentTable.status === "finished") {
    const isPlayer = currentTable.players?.includes(currentUser?.username);

    // Extract completed quests from glory column (if available in table data)
    const completedQuests = currentTable.boardState?.glory || [];

    return (
      <EndGame
        tableId={tableId}
        table={currentTable}
        completedQuests={completedQuests}
        playerCharacters={playerCharacters}
        isPlayer={isPlayer}
      />
    );
  }

  return (
    <GameBoard
      tableId={tableId}
      players={players}
      onBackToLobby={handleBackToLobby}
    />
  );
}

/**
 * Main application routes component
 */
const AppRoutes = () => {
  const { isAuthenticated } = useGame();

  // Optional debug logging in dev mode
  if (import.meta.env.VITE_DEV === "true") {
    // Using console.error because lint policy permits only error/warn for diagnostics
    console.error("[Debug] isAuthenticated:", isAuthenticated);
  }

  // If not authenticated, show login screen (dev log suppressed to reduce noise)
  if (!isAuthenticated) {
    return <LoginScreen onLoginSuccess={() => {}} />;
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] text-[var(--color-foreground)]">
      <Routes>
        {/* Initial route - redirects to tabletop if authenticated */}
        <Route path="/" element={<Navigate to="/tabletop" replace />} />

        {/* TableTop route */}
        <Route path="/tabletop" element={<TableTopHandler />} />

        {/* Cards view route */}
        <Route path="/cards" element={<CardsHandler />} />

        {/* Table routes with ID */}
        <Route
          path="/table/:tableId/lobby"
          element={
            <div>
              <AppHeader currentPath={window.location.pathname} />
              <main
                data-testid="App-main"
                className="container mx-auto px-4 py-6 bg-[var(--color-background)] text-[var(--color-foreground)]"
              >
                <Suspense fallback={<LoadingSpinner />}>
                  <LobbyWrapper />
                </Suspense>
              </main>
              <AppFooter />
            </div>
          }
        />

        <Route
          path="/table/:tableId/game"
          element={
            <div className="bg-game min-h-screen">
              <AppHeader currentPath={window.location.pathname} />
              <main
                data-testid="App-main"
                className="container mx-auto px-4 py-6 text-[var(--color-foreground)]"
              >
                <Suspense fallback={<LoadingSpinner />}>
                  <GameBoardWrapper />
                </Suspense>
              </main>
              <AppFooter />
            </div>
          }
        />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/tabletop" replace />} />
      </Routes>
    </div>
  );
};

/**
 * Main application component Guild for Glory.
 * Manages global application state with Context API and authentication.
 *
 * @component
 * @returns {JSX.Element} Application root component
 *
 * @example
 * // Application rendering
 * <App />
 *
 * @since 1.0.0
 * @author Team Building Dev Team
 */
function App() {
  const isProductionDomain = useIsProductionDomain();

  // Show Work in Progress page for production domain
  if (isProductionDomain) {
    return <WorkInProgress />;
  }

  return (
    <TranslationProvider>
      <ThemeProvider>
        <ColyseusProvider>
          <GameProvider>
            <AppWithRealtime />
            <ColyseusDebugPanel />
          </GameProvider>
        </ColyseusProvider>
        <Toaster position="top-right" richColors closeButton />
        {isProductionDomain && <SpeedInsights />}
      </ThemeProvider>
    </TranslationProvider>
  );
}

export default App;

// Component that has access to GameProvider context
function AppWithRealtime() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}
